<template>
  <v-dialog v-model="visible" lazy width="unset">
    <v-card>
      <v-container class="px-2 pt-2 pb-3" fluid>
        <v-layout>
          <v-flex>
            <v-combobox
              v-model="classification"
              append-outer-icon="save"
              autofocus
              chips
              clearable
              @click:append-outer="classifyItems"
              :hint="hint"
              item-text="displayableText"
              item-value="id"
              :items="classifications"
              label="Selecciona la clasificación a la cual mover los QMIs"
              :loading="loadingCreateClassification"
              open-on-clear
              :search-input.sync="searchInput"
            >
              <!-- A custom message to show when there is no classification that matches -->
              <template v-if="allowedToCreateNewClassifications" #no-data>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      Ninguna marca coincide con
                      <strong>{{ searchInput }}</strong>.
                      Presiona
                      <kbd>Enter</kbd> para crearla.
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
            </v-combobox>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapActions, mapMutations, mapState } = createNamespacedHelpers(
  "classifier"
);

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    classification: null,
    searchInput: null,
    visible: false
  }),
  computed: {
    ...mapState([
      "classificationsSellerId",
      "items",
      "loadingCreateClassification"
    ]),
    /**
     * A computable property that indicate if the user can create new classifications.
     * It can only to create new brands, so we check that the `classificationsSellerId`
     * is the 132.
     */
    allowedToCreateNewClassifications() {
      return this.classificationsSellerId === 132;
    },
    /**
     * Add a displayable text for the combobox to the classification from the store.
     */
    classifications() {
      return this.$store.state.classifier.classifications
			.filter((qmi) => {
				return qmi.enabled === true || qmi.enabled === null;
			})
			.map(
        classification => ({
          ...classification,
          displayableText: `${classification.breadcrumb ? classification.breadcrumb : classification.name} (${classification.id})`
        })
      )
    },
    /**
     * The hint to show in the combobox.
     */
    hint() {
      return `Solo los QMIs seleccionados como correctos se moverán a la clasificación ${
        this.classification ? this.classification.name : ""
      }`;
    }
  },
  watch: {
    /**
     * Check if the selected classification is a string to create a new one
     * and set it as the selected classification.
     */
    async classification() {
      /* If it's not a string there is nothing to do */
      if (typeof this.classification !== "string") return;
      /* If it does not allowed to create classification there is nothing to check */
      if (!this.allowedToCreateNewClassifications) {
        this.classification = null;
        return;
      }

      /* Try to create the classification and notify any error */
      try {
        const name = this.classification;
        const { id } = await this.createClassification(name);
        /* Move the selected items to this new classification */
        return this.changeClassificationOfSelectedItems(id).catch(() =>
          this.$notify.error(
            "No pudimos mover el item a la clasificación creada"
          )
        );
      } catch {
        this.$notify.error("No pudimos crear la clasificación");
        this.classification = null;
      }
    },
    value() {
      this.visible = this.value;
    },
    visible() {
      this.$emit("input", this.visible);
    }
  },
  methods: {
    ...mapActions([
      "changeClassificationOfSelectedItems",
      "createClassification"
    ]),
    ...mapMutations(["setItems"]),
    /**
     * Do not take in consideration the incorrect items and move the correct items
     * to the selected classification.
     */
    classifyItems() {
      /* Do not run the action if there is no classification */
      if (!this.classification) {
        return this.$notify.error(
          "Por favor selecciona una clasificación ya creada"
        );
      }

      /* Remove the "incorrect" ones */
      this.setItems(
        this.items.map(item => {
          if (item.correct) return item;
          return { ...item, correct: null };
        })
      );
      /* Classify the items */
      this.changeClassificationOfSelectedItems(this.classification.id);
      /* Close the dialog */
      this.visible = false;
      /* Reset the classification value */
      this.classification = null;
    }
  }
};
</script>